/**
 * Entries View - Admin view to review attorney entries for a specific date.
 *
 * Combines Entries table and Calendar views in a single tabbed layout.
 * Top row: Attorney selector (left) + Entries/Calendar tab pills (right).
 *
 * Uses the same EntriesTable component as the attorney view (DRY principle).
 * Differences from attorney view:
 * - No checkboxes (read-only)
 * - Click opens detail dialog instead of edit dialog
 * - No export/combine/delete actions
 */

import AdminEntryDetailDialog, { FeedbackPayload } from "../components/AdminEntryDetailDialog";
import { AdminTransaction, useCalendarTransactions, useProfessionals, useRestoreAndExport, useUserTransactions } from "../hooks/useAdminData";
import { Check, ChevronsUpDown } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/shared/components/ui/command";
// Reuse the same EntriesTable component (DRY)
import EntriesTable, { ColumnWidths, Entry, EntryStatus, SortDirection, SortingMode } from "../../attorney/components/EntriesTable/EntriesTable";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { useCallback, useMemo, useState } from "react";

import { ApplicationFilter } from "../../attorney/components/HeaderBar/ApplicationFilter";
import { Button } from "@/shared/components/ui/button";
import { DateRangePicker } from "../../attorney/components/HeaderBar";
import calendarStyles from "../../attorney/views/CalendarView/CalendarView.module.css";
import { cn } from "@/shared/lib/utils";
import { logger } from "@/shared/utils/logger";
// Nav arrow icons for month navigation
import navArrowLeftIcon from "@/assets/icons/navArrowLeft.svg";
import navArrowLeftWhiteIcon from "@/assets/icons/navArrowLeftWhite.svg";
import navArrowRightIcon from "@/assets/icons/navArrowRight.svg";
import navArrowRightWhiteIcon from "@/assets/icons/navArrowRightWhite.svg";
import styles from "./AdminViews.module.css";
import { toast } from "sonner";
import { useApi } from "@/shared/utils/request";
import { useApplicationFilter } from "../../attorney/hooks/useApplicationFilter";
import { useAuth } from "@workos-inc/authkit-react";
import { useDarkMode } from "@/shared/hooks/useDarkMode";
import { usePersistedState } from "../hooks/usePersistedState";

function formatDateForApi(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Format ISO date string to short display format (e.g., "Jan 16") */
function formatDateForDisplay(isoDate: string): string {
  // Handle both "YYYY-MM-DD" and full ISO strings like "2026-01-16T00:00:00.000Z"
  // Extract just the date part before any "T" separator
  const datePart = isoDate.split("T")[0];
  const [year, month, day] = datePart.split("-").map(Number);
  const date = new Date(year, month - 1, day); // Creates date in local time
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/**
 * Derive entry status from source table and exported flag.
 * - deleted_transactions → "deleted"
 * - transactions + exported=true → "exported"
 * - transactions + exported=false → "tracked"
 */
function deriveStatus(tx: AdminTransaction): EntryStatus {
  if (tx.source === "deleted_transactions") return "deleted";
  if (tx.exported) return "exported";
  return "tracked";
}

/** Transform AdminTransaction to Entry type for EntriesTable */
function toEntry(tx: AdminTransaction): Entry {
  return {
    id: tx.id,
    date: formatDateForDisplay(tx.date),
    matter: tx.matter_number || "",
    matter_id: tx.matter_id || undefined,
    matter_name: tx.matter_description || undefined,
    description: tx.description || "",
    task: tx.task_code || "",
    activity: tx.activity_code || undefined,
    document_name: tx.document_name || undefined,
    duration: tx.units,
    end_time: tx.end_time || undefined,
    application: tx.app_name,
    status: deriveStatus(tx),
  };
}

/**
 * Convert hour label like "9:00 AM" to 24-hour number for sorting.
 * "12:00 AM" = 0, "1:00 AM" = 1, ..., "12:00 PM" = 12, "1:00 PM" = 13, etc.
 */
function hourLabelTo24Hour(label: string): number {
  if (label === "Unknown") return 99; // Sort unknown to end
  const match = label.match(/^(\d+):00 (AM|PM)$/);
  if (!match) return 99;
  const hour = parseInt(match[1], 10);
  const isPM = match[2] === "PM";
  if (hour === 12) return isPM ? 12 : 0; // 12 PM = 12, 12 AM = 0
  return isPM ? hour + 12 : hour;
}

/** Group entries by hour, with configurable sort direction */
function groupEntriesByHour(entries: Entry[], direction: SortDirection = "desc"): Record<string, Entry[]> {
  const groups: Record<string, Entry[]> = {};
  entries.forEach((entry) => {
    let hourLabel = "Unknown";
    if (entry.end_time) {
      const parts = entry.end_time.split(":");
      if (parts.length >= 1) {
        const hours = parseInt(parts[0], 10);
        if (!isNaN(hours)) {
          const period = hours >= 12 ? "PM" : "AM";
          const displayHour = hours % 12 || 12;
          hourLabel = `${displayHour}:00 ${period}`;
        }
      }
    }
    if (!groups[hourLabel]) groups[hourLabel] = [];
    groups[hourLabel].push(entry);
  });

  const mult = direction === "asc" ? 1 : -1;
  const orderedGroups: Record<string, Entry[]> = {};
  Object.keys(groups)
    .sort((a, b) => mult * (hourLabelTo24Hour(a) - hourLabelTo24Hour(b)))
    .forEach((key) => { orderedGroups[key] = groups[key]; });
  return orderedGroups;
}

/** Group entries by matter, with configurable sort direction */
function groupEntriesByMatter(entries: Entry[], direction: SortDirection = "asc"): Record<string, Entry[]> {
  const groups: Record<string, Entry[]> = {};
  entries.forEach((entry) => {
    const key = entry.matter_name || entry.matter || "UNKNOWN";
    if (!groups[key]) groups[key] = [];
    groups[key].push(entry);
  });

  const mult = direction === "asc" ? 1 : -1;
  const sorted: Record<string, Entry[]> = {};
  Object.keys(groups)
    .sort((a, b) => mult * a.localeCompare(b))
    .forEach((key) => { sorted[key] = groups[key]; });
  return sorted;
}

const DEFAULT_COLUMN_WIDTHS: ColumnWidths = {
  date: 5,
  matter: 25,
  codes: 7,
  narrative: 55,
  units: 8,
};

// Date min width of 5% is enough for "Jan 16" format (~45px on most screens)
const COLUMN_MIN_WIDTHS: ColumnWidths = { date: 5, matter: 8, codes: 6, narrative: 10, units: 6 };
const COLUMN_ORDER: (keyof ColumnWidths)[] = ["date", "matter", "codes", "narrative", "units"];

/** Get today's date as YYYY-MM-DD string */
function getTodayString(): string {
  return formatDateForApi(new Date());
}

export default function EntriesView() {
  const { darkMode } = useDarkMode();
  const { user } = useAuth();

  // Active tab: entries or calendar
  const [activeTab, setActiveTab] = usePersistedState<"entries" | "calendar">(
    "admin:entries:tab",
    "entries"
  );

  // Persisted filter state (survives navigation, refresh, browser close)
  const [selectedDateStr, setSelectedDateStr] = usePersistedState<string>(
    "admin:entries:date",
    getTodayString()
  );
  const [selectedProfId, setSelectedProfId] = usePersistedState<string | null>(
    "admin:entries:professional",
    null
  );

  // Derived Date object for calendar component
  const selectedDate = useMemo(() => {
    const [year, month, day] = selectedDateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  }, [selectedDateStr]);

  // Helper to update date from Date object
  const setSelectedDate = (date: Date) => setSelectedDateStr(formatDateForApi(date));

  // Date navigation (previous/next day)
  const goToPreviousDay = useCallback(() => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 1);
    setSelectedDate(d);
  }, [selectedDate]);

  const goToNextDay = useCallback(() => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    setSelectedDate(d);
  }, [selectedDate]);

  // Application filter
  const {
    isVisible: isApplicationVisible,
    toggleApplication,
    filterEntries,
    isFilterActive,
    showAll: showAllApplications,
    hideAll: hideAllApplications,
  } = useApplicationFilter(user?.email || "");

  // Calendar tab: month being displayed (independent of entries date picker)
  const [calendarMonthDate, setCalendarMonthDate] = useState<Date>(new Date());

  // Non-persisted UI state
  const [attorneySearch, setAttorneySearch] = useState("");
  const [attorneyOpen, setAttorneyOpen] = useState(false);

  // Sorting state (matches attorney view pattern)
  const [sortingMode, setSortingMode] = usePersistedState<SortingMode>(
    "admin:entries:sortingMode",
    "time"
  );
  const [sortDirection, setSortDirection] = usePersistedState<SortDirection>(
    "admin:entries:sortDirection",
    "desc"
  );

  const handleSortByTime = useCallback(() => {
    if (sortingMode === "time") {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortingMode("time");
      setSortDirection("desc");
    }
  }, [sortingMode, sortDirection]);

  const handleSortByMatter = useCallback(() => {
    if (sortingMode === "matter") {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortingMode("matter");
      setSortDirection("asc");
    }
  }, [sortingMode, sortDirection]);

  // Dialog state
  const [selectedTx, setSelectedTx] = useState<AdminTransaction | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSavingFeedback, setIsSavingFeedback] = useState(false);

  // API for saving feedback and restoring deleted entries
  const { makeApiRequest } = useApi();
  const { restoreAndExport } = useRestoreAndExport();
  const [isRestoring, setIsRestoring] = useState(false);

  // Column widths with resize support (persisted like attorney view)
  const [columnWidths, setColumnWidths] = usePersistedState<ColumnWidths>(
    "admin:entries:columnWidths",
    DEFAULT_COLUMN_WIDTHS
  );

  // Data fetching - only show professionals with tracked time
  const { professionals, loading: profLoading } = useProfessionals({ activeOnly: true });
  const { transactions, setTransactions, loading: txLoading, refetch: refetchTransactions } = useUserTransactions(
    selectedDateStr, // Already in YYYY-MM-DD format
    selectedProfId,
    { includeDeleted: true } // Include deleted transactions for admin view
  );

  // Calendar tab data
  const { transactions: calendarTransactions, loading: calTxLoading } = useCalendarTransactions(
    calendarMonthDate,
    selectedProfId
  );

  // Generate unique day key for aggregation
  const dayKey = (d: Date) =>
    `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

  // Compute daily sums from calendar transactions
  const dailySums = useMemo(() => {
    const sums: Record<string, { unexported: number; exported: number }> = {};
    for (const tx of calendarTransactions) {
      const datePart = tx.date.split("T")[0];
      const [year, month, day] = datePart.split("-").map(Number);
      const d = new Date(year, month - 1, day);
      const k = dayKey(d);
      sums[k] = sums[k] || { unexported: 0, exported: 0 };
      if (tx.exported) {
        sums[k].exported += tx.units;
      } else {
        sums[k].unexported += tx.units;
      }
    }
    return sums;
  }, [calendarTransactions]);

  // Build 6x7 grid of days for the calendar month
  const calendarDays = useMemo(() => {
    const firstOfMonth = new Date(
      calendarMonthDate.getFullYear(),
      calendarMonthDate.getMonth(),
      1
    );
    const startDay = new Date(firstOfMonth);
    const startWeekday = startDay.getDay();
    startDay.setDate(startDay.getDate() - startWeekday);
    const days: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(startDay);
      d.setDate(startDay.getDate() + i);
      days.push(d);
    }
    return days;
  }, [calendarMonthDate]);

  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const goToPreviousMonth = () => {
    const d = new Date(calendarMonthDate);
    d.setMonth(d.getMonth() - 1);
    setCalendarMonthDate(d);
  };

  const goToNextMonth = () => {
    const d = new Date(calendarMonthDate);
    d.setMonth(d.getMonth() + 1);
    setCalendarMonthDate(d);
  };

  const goToToday = () => {
    setCalendarMonthDate(new Date());
  };

  // Click a day in calendar tab → switch to entries tab with that date
  const handleCalendarDayClick = (day: Date) => {
    if (!selectedProfId) return;
    setSelectedDate(day);
    setActiveTab("entries");
  };

  // Transform transactions to Entry format and apply sorting
  const entries = useMemo(() => {
    const mapped = [...transactions].map(toEntry);
    const mult = sortDirection === "asc" ? 1 : -1;

    switch (sortingMode) {
      case "matter":
        return mapped.sort((a, b) => {
          const matterA = a.matter_name || a.matter || "";
          const matterB = b.matter_name || b.matter || "";
          if (!matterA && matterB) return 1;
          if (matterA && !matterB) return -1;
          if (!matterA && !matterB) return 0;
          return mult * matterA.toLowerCase().localeCompare(matterB.toLowerCase());
        });
      case "time":
      default:
        return [...transactions]
          .sort((a, b) => mult * (a.start_time || "").localeCompare(b.start_time || ""))
          .map(toEntry);
    }
  }, [transactions, sortingMode, sortDirection]);

  // Apply application filter
  const filteredEntries = useMemo(() => filterEntries(entries), [entries, filterEntries]);

  // Group entries based on sort mode (matches attorney view pattern)
  const groupedEntries = useMemo(() => {
    if (filteredEntries.length === 0) return {};

    switch (sortingMode) {
      case "matter":
        return groupEntriesByMatter(filteredEntries, sortDirection);
      case "time":
      default:
        return groupEntriesByHour(filteredEntries, sortDirection);
    }
  }, [filteredEntries, sortingMode, sortDirection]);

  // Filter professionals by search
  const filteredProfs = useMemo(() => {
    if (!attorneySearch) return professionals.slice(0, 10);
    const term = attorneySearch.toLowerCase();
    return professionals.filter(p =>
      p.full_name.toLowerCase().includes(term) || p.email?.toLowerCase().includes(term)
    ).slice(0, 10);
  }, [professionals, attorneySearch]);

  const selectedProfName = professionals.find(p => p.prof_id === selectedProfId)?.full_name;

  // Handle row click - find original AdminTransaction and open dialog
  const handleRowClick = (entry: Entry) => {
    const tx = transactions.find(t => t.id === entry.id);
    if (tx) {
      setSelectedTx(tx);
      setDialogOpen(true);
    }
  };

  // Navigation: compute current index and bounds based on SORTED entries (not raw transactions)
  // This ensures navigation respects the current sort/filter applied by the user
  const currentIndex = useMemo(() => {
    if (!selectedTx || !filteredEntries.length) return -1;
    return filteredEntries.findIndex(e => e.id === selectedTx.id);
  }, [selectedTx, filteredEntries]);

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < filteredEntries.length - 1 && currentIndex !== -1;

  // Handle navigation between entries - use sorted/filtered entries array for correct order
  const handleNavigate = useCallback((direction: "previous" | "next") => {
    const newIndex = direction === "previous" ? currentIndex - 1 : currentIndex + 1;
    if (newIndex >= 0 && newIndex < filteredEntries.length) {
      const entryId = filteredEntries[newIndex].id;
      const tx = transactions.find(t => t.id === entryId);
      if (tx) {
        setSelectedTx(tx);
      }
    }
  }, [currentIndex, filteredEntries, transactions]);

  // Handle saving feedback
  const handleSaveFeedback = useCallback(async (payload: FeedbackPayload) => {
    setIsSavingFeedback(true);
    try {
      const response = await makeApiRequest({
        endpoint: `/admin/transactions/${payload.entryId}/feedback`,
        method: "POST",
        body: {
          approved: payload.approved,
          needs_improvement: payload.needsImprovement,
          deducted: payload.deducted,
          attorney_feedback: payload.attorneyFeedback,
          hourglass_feedback: payload.hourglassFeedback,
        },
      });

      if (response.error) {
        // Entry no longer exists in transactions table (exported or deleted)
        if (response.error.toLowerCase().includes("not found")) {
          toast.error("This entry has been exported or deleted and can no longer be updated", { duration: 5000 });
          throw new Error("Entry not found");
        }
        throw new Error(response.error);
      }

      // Update transactions state so navigation shows correct data
      setTransactions(prev =>
        prev.map(t =>
          t.id === payload.entryId
            ? {
                ...t,
                approved: payload.approved,
                needs_improvement: payload.needsImprovement,
                deducted: payload.deducted,
                attorney_feedback: payload.attorneyFeedback,
                hourglass_feedback: payload.hourglassFeedback,
              }
            : t
        )
      );

      logger.info("Feedback saved", { entryId: payload.entryId, approved: payload.approved, deducted: payload.deducted });
      toast.success("Feedback saved");
    } catch (error) {
      const errorMsg = "Failed to save feedback";
      logger.error(errorMsg, { error: error instanceof Error ? error.message : String(error) });
      toast.error(errorMsg, { duration: 5000 });
      throw error; // Re-throw so dialog knows save failed
    } finally {
      setIsSavingFeedback(false);
    }
  }, [makeApiRequest, setTransactions]);

  // Handle restore & export for deleted entries
  // isRestoring is managed here (not in the hook) so it covers the full operation including refetch
  const handleRestoreAndExport = useCallback(async (id: number): Promise<boolean> => {
    setIsRestoring(true);
    try {
      const success = await restoreAndExport(id);
      if (success) {
        await refetchTransactions();
      }
      return success;
    } finally {
      setIsRestoring(false);
    }
  }, [restoreAndExport, refetchTransactions]);

  // Column resize handler (same logic as TrackedView for full coupling)
  const handleCascadingResize = (resizingColumn: keyof ColumnWidths, deltaPercentage: number) => {
    const currentIndex = COLUMN_ORDER.indexOf(resizingColumn);
    if (currentIndex === -1) return;
    const newWidths = { ...columnWidths };
    const currentWidth = columnWidths[resizingColumn];
    const minWidth = COLUMN_MIN_WIDTHS[resizingColumn];
    if (deltaPercentage > 0) {
      let availableSpace = 0;
      for (let i = currentIndex + 1; i < COLUMN_ORDER.length; i++) {
        const key = COLUMN_ORDER[i];
        availableSpace += Math.max(0, columnWidths[key] - COLUMN_MIN_WIDTHS[key]);
      }
      const actualExpansion = Math.min(deltaPercentage, availableSpace);
      if (actualExpansion <= 0) return;
      newWidths[resizingColumn] = currentWidth + actualExpansion;
      let remaining = actualExpansion;
      for (let i = currentIndex + 1; i < COLUMN_ORDER.length && remaining > 0.01; i++) {
        const key = COLUMN_ORDER[i];
        const canRemove = Math.max(0, columnWidths[key] - COLUMN_MIN_WIDTHS[key]);
        const toRemove = Math.min(remaining, canRemove);
        if (toRemove > 0.01) {
          newWidths[key] = columnWidths[key] - toRemove;
          remaining -= toRemove;
        }
      }
    } else {
      const maxShrinkage = currentWidth - minWidth;
      const actualShrinkage = Math.max(deltaPercentage, -maxShrinkage);
      if (actualShrinkage >= 0) return;
      newWidths[resizingColumn] = currentWidth + actualShrinkage;
      const spaceToGive = -actualShrinkage;
      for (let i = currentIndex + 1; i < COLUMN_ORDER.length; i++) {
        newWidths[COLUMN_ORDER[i]] = columnWidths[COLUMN_ORDER[i]] + spaceToGive;
        break;
      }
    }
    setColumnWidths(newWidths);
  };

  // Empty state message (matches attorney view pattern)
  const emptyMessage = useMemo(() => {
    if (!selectedProfId) return "Select an attorney to view their entries.";
    if (filteredEntries.length > 0) return undefined;
    if (entries.length > 0) return "No entries match the current filter.";
    return "No entries for this date.";
  }, [selectedProfId, filteredEntries.length, entries.length]);

  // Dummy handlers for checkbox props (admin view has no selection)
  const noop = () => {};
  const emptySet = new Set<number>();

  return (
    <div className={cn("flex flex-col gap-4 p-6 bg-background h-full", styles.adminViewWrapper)}>
      {/* Top row: Attorney selector (left) + Tab pills (right) */}
      <div className="flex items-center justify-between">
        {/* Professional Dropdown */}
        <Popover open={attorneyOpen} onOpenChange={setAttorneyOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-[220px] justify-between">
              {selectedProfName || "Select attorney..."}
              <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[280px] p-0">
            <Command>
              <CommandInput placeholder="Search..." value={attorneySearch} onValueChange={setAttorneySearch} />
              <CommandList>
                <CommandEmpty>{profLoading ? "Loading..." : "No attorneys found."}</CommandEmpty>
                <CommandGroup>
                  {filteredProfs.map(prof => (
                    <CommandItem
                      key={prof.prof_id}
                      value={`${prof.full_name} ${prof.email ?? ""}`}
                      onSelect={() => { setSelectedProfId(prof.prof_id); setAttorneyOpen(false); setAttorneySearch(""); }}
                    >
                      <Check className={cn("mr-2 h-4 w-4", selectedProfId === prof.prof_id ? "opacity-100" : "opacity-0")} />
                      <div className="flex flex-col">
                        <span>{prof.full_name}</span>
                        <span className="text-xs text-muted-foreground">{prof.email}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Tab pills */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "entries" | "calendar")}>
          <TabsList>
            <TabsTrigger value="entries">Entries</TabsTrigger>
            <TabsTrigger value="calendar">Calendar</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Tab content */}
      {activeTab === "entries" ? (
        <>
          {/* Date picker + Application filter row */}
          <div className="flex items-center gap-3">
            <ApplicationFilter
              isVisible={isApplicationVisible}
              onToggle={toggleApplication}
              isFilterActive={isFilterActive}
              onShowAll={showAllApplications}
              onHideAll={hideAllApplications}
            />
            <DateRangePicker
              date={selectedDate}
              onDateChange={setSelectedDate}
              compact
              onPrevious={goToPreviousDay}
              onNext={goToNextDay}
            />
          </div>

          {/* Entries table */}
          <div className="border border-border rounded-lg overflow-hidden flex-1 flex flex-col">
            {txLoading ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                Loading...
              </div>
            ) : (
              <EntriesTable
                entries={filteredEntries}
                groupedEntries={groupedEntries}
                columnWidths={columnWidths}
                onResizeColumn={(key, w) => setColumnWidths({ ...columnWidths, [key]: w })}
                onCascadingResize={handleCascadingResize}
                checkedRows={emptySet}
                selectAllChecked={false}
                selectAllIndeterminate={false}
                onToggleSelectAll={noop}
                onToggleRow={noop}
                onRowClick={selectedProfId ? handleRowClick : undefined}
                showCheckboxes={false}
                mode="entries"
                cellPadding={12}
                sortingMode={sortingMode}
                sortDirection={sortDirection}
                onSortByTime={handleSortByTime}
                onSortByMatter={handleSortByMatter}
                emptyMessage={emptyMessage}
              />
            )}
          </div>

          {/* Detail Dialog */}
          <AdminEntryDetailDialog
            entry={selectedTx}
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            onNavigate={handleNavigate}
            hasPrevious={hasPrevious}
            hasNext={hasNext}
            onSaveFeedback={handleSaveFeedback}
            isSaving={isSavingFeedback}
            onRestoreAndExport={handleRestoreAndExport}
            isRestoring={isRestoring}
          />
        </>
      ) : (
        /* Calendar tab content */
        <>
          <div className="border border-border rounded-lg overflow-hidden flex-1 flex flex-col">
            {!selectedProfId ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                Select an attorney to view their calendar
              </div>
            ) : calTxLoading ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground">
                Loading...
              </div>
            ) : (
              <div className={calendarStyles.calendarView}>
                {/* Calendar Header */}
                <div className={calendarStyles.calendarHeader}>
                  <span className={calendarStyles.calendarHeaderLabel}>
                    {calendarMonthDate.toLocaleDateString(undefined, {
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                  <div className={calendarStyles.calendarHeaderButtonWrapper}>
                    <button
                      className={calendarStyles.calendarPageButton}
                      onClick={goToPreviousMonth}
                    >
                      <img
                        src={darkMode ? navArrowLeftWhiteIcon : navArrowLeftIcon}
                        alt="Previous"
                      />
                    </button>
                    <button
                      className={`${calendarStyles.calendarPageButton} ${calendarStyles.calendarTodayButton}`}
                      onClick={goToToday}
                    >
                      Today
                    </button>
                    <button
                      className={calendarStyles.calendarPageButton}
                      onClick={goToNextMonth}
                    >
                      <img
                        src={darkMode ? navArrowRightWhiteIcon : navArrowRightIcon}
                        alt="Next"
                      />
                    </button>
                  </div>
                </div>

                {/* Calendar Grid */}
                <div className={calendarStyles.calendarGrid}>
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                    <div key={day} className={calendarStyles.calendarWeekdayHeader}>
                      {day}
                    </div>
                  ))}

                  {calendarDays.map((currDay, index) => {
                    const isCurrentMonth =
                      currDay.getMonth() === calendarMonthDate.getMonth();
                    const isWeekend =
                      currDay.getDay() === 0 || currDay.getDay() === 6;
                    const key = dayKey(currDay);
                    const unexported = (dailySums[key]?.unexported || 0).toFixed(1);
                    const exported = (dailySums[key]?.exported || 0).toFixed(1);

                    return (
                      <button key={index} onClick={() => handleCalendarDayClick(currDay)}>
                        <div
                          className={`${calendarStyles.calendarCard} ${
                            !isCurrentMonth
                              ? calendarStyles.calendarOutsideMonthCard
                              : ""
                          } ${isWeekend ? calendarStyles.calendarWeekendCard : ""}`}
                          data-today={isSameDay(currDay, new Date())}
                        >
                          <div className={calendarStyles.cardDate}>
                            {currDay.getDate()}
                          </div>
                          <div
                            className={`${calendarStyles.cardMetric} ${calendarStyles.cardMetricUnits}`}
                          >
                            {unexported} hrs to export
                          </div>
                          <div
                            className={`${calendarStyles.cardMetric} ${calendarStyles.cardMetricUnits}`}
                          >
                            {exported} hrs exported
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
